import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * The stronger of the two delete-confirmation patterns — used only for
 * Client and Engagement deletion, both irreversible and both capable
 * of wiping a large amount of real coaching history in one action.
 * The Delete button stays disabled until the typed text exactly
 * matches `confirmValue` (the client's name / the Engagement's title);
 * the same value is re-checked server-side too.
 */
export function TypeToConfirmDialog({ open, onOpenChange, title, description, confirmValue, confirmLabel, onConfirm, isPending, error, children, testId }) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const matches = typed.trim() === confirmValue;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testId || "type-to-confirm-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {children}

        <div className="space-y-1.5">
          <Label className="text-xs text-ink-soft">
            Type <span className="font-semibold text-ink">{confirmValue}</span> {confirmLabel || "to confirm"}
          </Label>
          <Input
            value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus
            data-testid="type-to-confirm-input"
          />
        </div>

        {error && <p className="text-sm text-red-600" data-testid="type-to-confirm-error">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="type-to-confirm-cancel">Cancel</Button>
          <Button
            variant="destructive" disabled={!matches || isPending} onClick={onConfirm}
            data-testid="type-to-confirm-submit"
          >
            {isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
